import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Button, CircularProgress, Chip, Alert, Snackbar, Card, CardContent, Grid, Avatar, Divider } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { motion } from 'framer-motion';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HistoryIcon from '@mui/icons-material/History';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const accountPageSx = {
  py: { xs: 5, md: 7 },
  '& .MuiCard-root': {
    background: (theme) => `${theme.palette.background.paper} !important`,
    backgroundImage: 'none !important',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '24px !important',
    boxShadow: (theme) =>
      theme.palette.mode === 'dark'
        ? '0 24px 80px rgba(0, 0, 0, 0.24)'
        : '0 24px 80px rgba(82, 107, 170, 0.12)',
  },
};

export default function Subscription() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasActiveSubscription, hasLifetimeAccess } = useSubscriptionStatus();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    async function fetchSubscription() {
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        
        // //console.log('Subscription data:', {
        //   data,
        //   customerId: data?.customer_id,
        //   metadata: data?.metadata
        // });
        
        setSubscription(data);
      } catch (err) {
        //console.error('Error fetching subscription:', err);
        setError('Failed to load subscription data');
        setShowError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchSubscription();
  }, [user, navigate]);

  const handleUpgrade = () => {
    navigate('/purchase');
  };

  const handleManageSubscription = async () => {
    try {
      // Debug log to see what data we have
      // //console.log('Subscription data for portal:', {
      //   subscription,
      //   metadata: subscription?.metadata,
      //   stripeCustomerId: subscription?.stripe_customer_id,
      //   metadataCustomer: subscription?.metadata?.customer
      // });

      const customerId = subscription?.stripe_customer_id || 
                        subscription?.metadata?.customer;

      if (!customerId) {
        //console.error('No customer ID found in:', subscription);
        setError('Customer ID not found. Please contact support.');
        setShowError(true);
        return;
      }
      //console.log('customerId', customerId);
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { customerId }
      });
      //console.log('data', data);
      //console.log('error', error);
      if (error) {
        //console.error('Portal session error:', error);
        throw error;
      }
      if (!data?.url) {
        //console.error('No portal URL in response:', data);
        throw new Error('No portal URL received');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (err) {
      //console.error('Error creating portal session:', err);
      setError('Failed to open customer portal. Please try again later.');
      setShowError(true);
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const getMembershipColor = () => {
    if (hasLifetimeAccess) return '#b7791f';
    if (hasActiveSubscription) return '#5168ff';
    return '#888';
  };

  const getMembershipIcon = () => {
    if (hasLifetimeAccess) return <DiamondIcon />;
    if (hasActiveSubscription) return <StarIcon />;
    return <ShoppingCartIcon />;
  };

  const getSubscriptionStatus = () => {
    if (hasLifetimeAccess) return 'Lifetime Access';
    if (hasActiveSubscription) return 'Active Subscription';
    return 'No Active Subscription';
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 }, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <CircularProgress sx={{ color: 'secondary.main' }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Loading subscription details...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={accountPageSx}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header Section */}
          <motion.div variants={fadeInUp}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip label="Billing" size="small" sx={{ mb: 2, bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 700 }} />
              <Typography 
                variant="h3" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 850,
                  mb: 2,
                  letterSpacing: 0,
                }}
              >
                Subscription and billing.
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Manage your subscription and billing preferences
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {/* Subscription Status Card */}
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card 
                  sx={{ 
                    background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(115, 213, 255, 0.05) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(81, 104, 255, 0.2)',
                    borderRadius: 3,
                    height: '100%'
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar 
                        sx={{ 
                          width: 64, 
                          height: 64, 
                          bgcolor: getMembershipColor(),
                          mr: 2
                        }}
                      >
                        {getMembershipIcon()}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                          Current Plan
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {getSubscriptionStatus()}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                      <Chip 
                        label={hasActiveSubscription ? 'Active' : 'Inactive'}
                        sx={{ 
                          bgcolor: hasActiveSubscription ? 'rgba(21, 144, 95, 0.2)' : 'rgba(136, 136, 136, 0.2)',
                          color: hasActiveSubscription ? '#15905f' : '#888',
                          fontWeight: 'bold'
                        }}
                      />
                      {hasLifetimeAccess && (
                        <Chip 
                          label="Lifetime"
                          sx={{ 
                            bgcolor: 'rgba(255, 215, 0, 0.2)',
                            color: '#b7791f',
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {hasLifetimeAccess 
                        ? 'You have unlimited access to all premium features forever!'
                        : hasActiveSubscription 
                        ? 'Your subscription is active and billing is managed through Stripe.'
                        : 'You currently have a free account. Upgrade to access premium features.'
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Management Actions Card */}
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card 
                  sx={{ 
                    background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(115, 213, 255, 0.05) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(81, 104, 255, 0.2)',
                    borderRadius: 3,
                    height: '100%'
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <ManageAccountsIcon sx={{ fontSize: 40, color: '#5168ff', mr: 2 }} />
                      <Typography variant="h6" sx={{ color: '#5168ff', fontWeight: 'bold' }}>
                        Quick Actions
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {hasActiveSubscription ? (
                        <>
                          <Button
                            variant="contained"
                            startIcon={<CreditCardIcon />}
                            onClick={handleManageSubscription}
                            sx={{
                              background: '#0a0d14',
                              color: '#fff',
                              fontWeight: 'bold',
                              '&:hover': {
                                background: '#05070c',
                              }
                            }}
                          >
                            Manage Billing
                          </Button>
                          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            Update payment methods, view invoices, and manage your subscription
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            startIcon={<ShoppingCartIcon />}
                            onClick={handleUpgrade}
                            sx={{
                              background: '#0a0d14',
                              color: '#fff',
                              fontWeight: 'bold',
                              '&:hover': {
                                background: '#05070c',
                              }
                            }}
                          >
                            Upgrade to Premium
                          </Button>
                          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            Access all premium features and content
                          </Typography>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Billing Information Card - Only show if has active subscription */}
            {hasActiveSubscription && (
              <Grid item xs={12}>
                <motion.div variants={fadeInUp}>
                  <Card 
                    sx={{ 
                      background: 'linear-gradient(135deg, rgba(81, 104, 255, 0.1) 0%, rgba(115, 213, 255, 0.05) 100%)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(81, 104, 255, 0.2)',
                      borderRadius: 3
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <HistoryIcon sx={{ fontSize: 40, color: '#5168ff', mr: 2 }} />
                        <Box>
                          <Typography variant="h6" sx={{ color: '#5168ff', fontWeight: 'bold' }}>
                            Billing & History
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Access your complete billing history and invoices
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 3, borderColor: 'rgba(81, 104, 255, 0.2)' }} />
                      
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                        View your billing history, download invoices, update payment methods, and manage your subscription through our secure Stripe Customer Portal.
                      </Typography>
                      
                      <Button 
                        variant="outlined"
                        startIcon={<CreditCardIcon />}
                        onClick={handleManageSubscription}
                        sx={{ 
                          borderColor: '#5168ff',
                          color: '#5168ff',
                          '&:hover': {
                            borderColor: '#73d5ff',
                            bgcolor: 'rgba(81, 104, 255, 0.1)'
                          }
                        }}
                      >
                        Open Customer Portal
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            )}
          </Grid>
        </motion.div>
      </Container>

      <Snackbar 
        open={showError} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseError} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
} 
